import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "../../lib/api";
import { downloadFile } from "../../lib/download";
import { Card, Badge } from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import MomoPhoneField from "../../components/MomoPhoneField";
import { useAuth } from "../../context/AuthContext";
import { appointmentDue, cancellationTerms, formatDay, formatPrice, todayISO } from "../../lib/utils";

const STATUS = {
  pending: { label: "Waiting for confirmation", variant: "warning" },
  confirmed: { label: "Confirmed", variant: "primary" },
  completed: { label: "Completed", variant: "accent" },
  cancelled: { label: "Cancelled", variant: "default" },
  no_show: { label: "No-show", variant: "default" },
};

const PAYMENT = {
  paid: "Paid",
  deposit_paid: "Deposit paid",
  refunded: "Refunded",
  partially_refunded: "Partly refunded",
};

const REFUND = {
  requested: "being reviewed by SHUWA",
  approved: "approved",
  processed: "paid back",
  rejected: "declined",
};

const LATE_OPTIONS = [5, 15, 30];
// How long after the start time lateness can still be reported.
const LATE_GRACE_MINUTES = 30;

const DUE = {
  passed: () => "The time of this appointment has passed",
  minutes: (n) => `Starts in ${n} min`,
  today: () => "Due today",
  tomorrow: () => "Due tomorrow",
  days: (n) => `In ${n} days`,
};

const errorMessage = (e, fallback) => e?.response?.data?.message || fallback;

function CancelPanel({ appointment, onClose, onCancelled }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .get(`/appointments/${appointment.id}/checkout-summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary({}));
  }, [appointment.id]);

  const confirm = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/appointments/${appointment.id}/cancel`);
      const parts = ["Your appointment was cancelled."];
      if (data.cancellation_fee_charged > 0) parts.push(`A cancellation fee of ${formatPrice(data.cancellation_fee_charged)} applies.`);
      if (data.refund_amount > 0) parts.push(`Your refund of ${formatPrice(data.refund_amount)} has been opened — we'll email you when it's paid.`);
      onCancelled(parts.join(" "));
    } catch (e) {
      setError(errorMessage(e, "Could not cancel this appointment."));
      setBusy(false);
    }
  };

  const free = summary?.within_free_cancellation_window;
  const paid = Number(summary?.amount_paid || 0);
  const fee = Number(summary?.cancellation_fee || 0);

  return (
    <div className="mt-3 border-t border-border pt-3 text-sm">
      {!summary ? (
        <p className="text-muted-foreground">Checking the cancellation terms…</p>
      ) : (
        <>
          <p className="font-semibold text-slate-900">Cancel this appointment?</p>
          <p className="mt-1 text-muted-foreground">
            {free
              ? `Cancellation is free until ${summary.free_cancellation_hours} hours before your appointment.${
                  paid > 0 ? ` The ${formatPrice(paid)} you paid will be refunded.` : ""
                }`
              : fee > 0
              ? `It is less than ${summary.free_cancellation_hours} hours before your appointment, so the business's cancellation fee of ${formatPrice(fee)} applies${
                  paid > fee ? ` and the remaining ${formatPrice(paid - fee)} will be refunded` : ""
                }.`
              : `It is less than ${summary.free_cancellation_hours ?? 24} hours before your appointment.${
                  paid > 0 ? ` SHUWA will review the refund of the ${formatPrice(paid)} you paid.` : ""
                }`}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="danger" disabled={busy} onClick={confirm}>
              {busy ? "Cancelling…" : "Yes, cancel it"}
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
              Keep my appointment
            </Button>
          </div>
        </>
      )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function ReschedulePanel({ appointment, onClose, onMoved }) {
  const [date, setDate] = useState(appointment.appointment_date?.slice(0, 10) ?? todayISO());
  const [slots, setSlots] = useState(null);
  const [time, setTime] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const duration = (appointment.services || []).reduce((sum, s) => sum + Number(s.duration_minutes_snapshot || 0), 0) || 30;
  // A string, so the slots aren't refetched each time the list re-renders.
  const serviceIds = (appointment.services || []).map((s) => s.service_id).join(",");

  // Only real open slots for the same professional can be picked.
  useEffect(() => {
    setSlots(null);
    setTime(null);
    let stale = false;
    const load = () =>
      api
        .get(`/businesses/${appointment.business?.slug}/availability`, {
          params: {
            date,
            staff_id: appointment.staff_id || undefined,
            duration_minutes: duration,
            service_ids: serviceIds ? serviceIds.split(",") : [],
          },
        })
        .then(({ data }) => {
          if (stale) return;
          const open = [...new Set(data.staff.flatMap((s) => s.slots))].sort();
          setSlots(open);
          setTime((current) => (current && !open.includes(current) ? null : current));
        })
        .catch(() => !stale && setSlots((current) => current ?? []));
    load();
    // Reloaded every minute, so times that pass or get booked meanwhile drop off.
    const timer = setInterval(load, 60000);
    return () => {
      stale = true;
      clearInterval(timer);
    };
  }, [date, appointment.business?.slug, appointment.staff_id, duration, serviceIds]);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/appointments/${appointment.id}/reschedule`, { appointment_date: date, start_time: time });
      onMoved(`Your appointment was moved to ${formatDay(date)} at ${time}.`);
    } catch (e) {
      setError(errorMessage(e, "Could not reschedule to that time."));
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="text-sm font-semibold text-slate-900">Pick a new date and time</p>
      <Input type="date" value={date} min={todayISO()} onChange={(e) => setDate(e.target.value)} className="mt-2 w-full sm:w-auto" />
      <div className="mt-3 flex flex-wrap gap-2">
        {slots === null ? (
          <p className="text-sm text-muted-foreground">Loading times…</p>
        ) : slots.length === 0 ? (
          <p className="text-sm text-muted-foreground">No open times that day. Try another date.</p>
        ) : (
          slots.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTime(t)}
              className={`rounded-full border px-3 py-1.5 text-sm ${time === t ? "border-primary bg-primary text-white" : "border-border"}`}
            >
              {t}
            </button>
          ))
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" disabled={!time || busy} onClick={submit}>
          {busy ? "Moving…" : "Confirm new time"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

function ReviewPanel({ appointment, onClose, onSaved }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/reviews", { appointment_id: appointment.id, rating, comment: comment.trim() || undefined });
      onSaved("Thanks for your review!");
    } catch (e) {
      setError(errorMessage(e, "Could not save your review."));
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="text-sm font-semibold text-slate-900">How was your visit to {appointment.business?.name}?</p>
      <div className="mt-2 flex gap-1" role="radiogroup" aria-label="Rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} aria-label={`${n} star${n > 1 ? "s" : ""}`} onClick={() => setRating(n)}>
            <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <textarea
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="Tell others about it (optional)"
        className="mt-2 w-full rounded-xl border border-border p-3 text-sm outline-none focus:border-primary"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <Button size="sm" disabled={!rating || busy} onClick={submit}>
          {busy ? "Sending…" : "Send review"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Not now
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

// Pay (or finish paying) for a booking — e.g. after a payment that failed
// at checkout, or a deposit that is still due.
function PayPanel({ appointment, onClose, onPaid }) {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [provider, setProvider] = useState(null);
  const [phone, setPhone] = useState("");
  const [testOutcome, setTestOutcome] = useState("success");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setConfig(data)).catch(() => setConfig({}));
    api.get(`/appointments/${appointment.id}/checkout-summary`).then(({ data }) => setSummary(data)).catch(() => setSummary({}));
    api.get("/wallet").then(({ data }) => setWallet(Number(data.balance))).catch(() => setWallet(null));
  }, [appointment.id]);

  if (!config || !summary) {
    return <p className="mt-3 border-t border-border pt-3 text-sm text-muted-foreground">Loading payment options…</p>;
  }

  const depositDue = summary.deposit_required ? Number(summary.deposit_amount) : 0;
  const amount = depositDue || Number(summary.balance_due || 0);
  const options = [
    ...(config.mobile_money?.mtn ? [{ id: "mtn", label: "MTN Mobile Money" }] : []),
    ...(config.mobile_money?.airtel ? [{ id: "airtel", label: "Airtel Money" }] : []),
    ...(wallet !== null ? [{ id: "wallet", label: `SHUWA wallet (${formatPrice(wallet)})` }] : []),
    ...(depositDue > 0 ? [] : [{ id: "cash", label: "Pay at store" }]),
    ...(config.test_mode ? [{ id: "test", label: "Test payment" }] : []),
  ];
  const selected = options.some((o) => o.id === provider) ? provider : options[0]?.id;

  const pay = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/appointments/${appointment.id}/pay`, {
        provider: selected,
        payment_type: depositDue > 0 ? "deposit" : undefined,
        phone: ["mtn", "airtel"].includes(selected) && phone ? phone : undefined,
        test_outcome: selected === "test" ? testOutcome : undefined,
      });

      onPaid(data.message || "Payment sent.");
    } catch (e) {
      setError(errorMessage(e, e?.message || "Payment could not be processed."));
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <p className="text-sm font-semibold text-slate-900">
        {depositDue > 0 ? `Pay the ${formatPrice(depositDue)} deposit to confirm` : `Pay ${formatPrice(amount)}`}
      </p>
      {amount <= 0 || options.length === 0 ? (
        <p className="mt-1 text-sm text-muted-foreground">
          {amount <= 0 ? "There is nothing left to pay on this appointment." : "No online payment method is available right now."}
        </p>
      ) : (
        <>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setProvider(o.id)}
                className={`rounded-xl border p-3 text-left text-sm ${selected === o.id ? "border-primary bg-primary/5" : "border-border"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {["mtn", "airtel"].includes(selected) && (
            <MomoPhoneField accountPhone={user?.phone} value={phone} onChange={setPhone} className="mt-2" />
          )}
          {selected === "test" && (
            <div className="mt-2 flex gap-2">
              {[
                { id: "success", label: "Approve" },
                { id: "failed", label: "Decline" },
              ].map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setTestOutcome(o.id)}
                  className={`flex-1 rounded-xl border p-2 text-sm ${testOutcome === o.id ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
          {selected !== "cash" && Number(summary.service_fee) > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Includes the {formatPrice(summary.service_fee)} SHUWA booking fee. Paying at the store carries no fee.
            </p>
          )}
        </>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {amount > 0 && options.length > 0 && (
          <Button size="sm" disabled={busy} onClick={pay}>
            {busy ? "Processing…" : selected === "cash" ? "I'll pay at the store" : "Pay now"}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy} onClick={onClose}>
          Close
        </Button>
      </div>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}

export default function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Which appointment has a panel open, and which one: { id, panel }.
  const [open, setOpen] = useState(null);
  const [notice, setNotice] = useState("");
  const [reportingLate, setReportingLate] = useState(false);

  const load = () => {
    api
      .get("/appointments")
      .then(({ data }) => setAppointments(data.data || []))
      .catch(() => setAppointments([]))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggle = (id, panel) => setOpen(open?.id === id && open.panel === panel ? null : { id, panel });
  const done = (message) => {
    setOpen(null);
    setNotice(message);
    load();
  };

  const reportLate = async (id, minutes) => {
    setReportingLate(true);
    try {
      await api.post(`/appointments/${id}/running-late`, { minutes });
      done("Thanks — the business has been told you're running late.");
    } finally {
      setReportingLate(false);
    }
  };

  if (loading) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div>
      <h1 className="mb-4 font-montserrat text-2xl font-bold text-slate-900">Appointments</h1>
      {notice && (
        <p className="mb-4 rounded-xl bg-accent p-3 text-sm text-accent-foreground" role="status">
          {notice}
        </p>
      )}
      {appointments.length === 0 ? (
        <p className="text-muted-foreground">You don't have any appointments yet.</p>
      ) : (
        <div className="space-y-3">
          {appointments.map((a) => {
            const active = ["pending", "confirmed"].includes(a.status);
            const status = STATUS[a.status] ?? { label: a.status, variant: "default" };
            const where = [a.business?.address, a.business?.city].filter(Boolean).join(", ");
            const balance = Number(a.total_amount) - Number(a.amount_paid || 0);
            // The booking fee only exists on bookings paid online.
            const hasFeeReceipt = Number(a.service_fee) > 0 && Number(a.amount_paid) > 0 && a.payment_status !== "refunded";
            const review = a.review?.[0];
            const panel = open?.id === a.id ? open.panel : null;
            const close = () => setOpen(null);
            const due = active ? appointmentDue(a.appointment_date, a.start_time) : null;
            // Once the time has passed, only paying is left to the customer —
            // and, for a short while, saying they are running late.
            const changeable = active && due?.key !== "passed";
            const canReportLate = changeable || (active && due?.key === "passed" && due.count < LATE_GRACE_MINUTES);

            return (
              <Card key={a.id} className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900">{a.business?.name}</p>
                    <p className="text-sm text-slate-700">
                      {formatDay(a.appointment_date)} · {a.start_time?.slice(0, 5)}
                    </p>
                    {due && (
                      <p
                        className={`text-sm font-semibold ${
                          due.key === "passed" ? "text-destructive" : due.key === "days" ? "text-muted-foreground" : "text-amber-700"
                        }`}
                      >
                        {DUE[due.key](due.count)}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {a.services?.map((s) => s.name_snapshot).join(", ")}
                      {a.staff?.name ? ` · with ${a.staff.name}` : ""}
                    </p>
                    {where && <p className="text-sm text-muted-foreground">{where}</p>}
                    {a.customer_late_minutes > 0 && active && (
                      <p className="mt-1 text-xs text-destructive">
                        You reported {a.customer_late_minutes} min late.
                        {a.bumped_for_lateness ? " The provider may move you behind the next customer." : " Your appointment remains active."}
                      </p>
                    )}
                    {(a.refunds || []).map((r) => (
                      <p key={r.id} className="mt-1 text-xs text-muted-foreground">
                        Refund of {formatPrice(r.amount)}: {REFUND[r.status] ?? r.status}
                      </p>
                    ))}
                    {review && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        You rated this visit
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-3.5 w-3.5 ${n <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
                        ))}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <span className="font-semibold">{formatPrice(a.total_amount)}</span>
                    <Badge variant={status.variant}>{status.label}</Badge>
                    {PAYMENT[a.payment_status] && <Badge variant="accent">{PAYMENT[a.payment_status]}</Badge>}
                    {active && balance > 0 && <Badge variant="warning">{formatPrice(balance)} to pay</Badge>}
                  </div>
                </div>

                {(canReportLate || (active && balance > 0) || (a.status === "completed" && !review) || hasFeeReceipt) && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {hasFeeReceipt && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadFile(`/appointments/${a.id}/receipt`, "SHUWA-booking-fee-receipt.pdf").catch(() => {})}
                      >
                        Booking fee receipt (PDF)
                      </Button>
                    )}
                    {active && balance > 0 && (
                      <Button size="sm" onClick={() => toggle(a.id, "pay")}>
                        {a.payment_status === "deposit_paid" ? "Pay the rest" : "Pay now"}
                      </Button>
                    )}
                    {canReportLate && (
                      <Button size="sm" variant="outline" onClick={() => toggle(a.id, "late")}>
                        Running late?
                      </Button>
                    )}
                    {changeable && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => toggle(a.id, "reschedule")}>
                          Reschedule
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggle(a.id, "cancel")}>
                          Cancel
                        </Button>
                      </>
                    )}
                    {a.status === "completed" && !review && (
                      <Button size="sm" onClick={() => toggle(a.id, "review")}>
                        Leave a review
                      </Button>
                    )}
                  </div>
                )}

                {panel === "late" && (
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-3">
                    {LATE_OPTIONS.map((minutes) => (
                      <Button key={minutes} size="sm" variant="outline" disabled={reportingLate} onClick={() => reportLate(a.id, minutes)}>
                        I'm {minutes} min late
                      </Button>
                    ))}
                  </div>
                )}
                {panel === "pay" && <PayPanel appointment={a} onClose={close} onPaid={done} />}
                {panel === "reschedule" && <ReschedulePanel appointment={a} onClose={close} onMoved={done} />}
                {panel === "cancel" && <CancelPanel appointment={a} onClose={close} onCancelled={done} />}
                {panel === "review" && <ReviewPanel appointment={a} onClose={close} onSaved={done} />}

                {changeable && a.cancellation && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    {cancellationTerms(a.cancellation.free_hours, a.cancellation.fee)}
                  </p>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
